import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Detective, Scissors } from "@phosphor-icons/react";

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function BillingHealthCard() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [fixing, setFixing] = useState(null);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.get("/admin/billing/duplicate-subs");
      setResult(data);
      if (data.duplicates.length === 0 && (data.zombies || []).length === 0) toast.success(`Scanned ${data.users_scanned} users — no billing problems found.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const fix = async (email) => {
    if (!window.confirm(
      `Cancel the OLDER duplicate subscription(s) for ${email}?\n\nTheir newest subscription is kept; extras are canceled immediately so they stop being charged twice.`
    )) return;
    setFixing(email);
    try {
      const { data } = await api.post(`/admin/billing/cancel-extra-subs/${encodeURIComponent(email)}`);
      toast.success(`Canceled ${data.canceled.length} duplicate sub(s) for ${email}. Kept ${data.kept}.`);
      scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fix failed");
    } finally {
      setFixing(null);
    }
  };

  const [manualEmail, setManualEmail] = useState("");

  const cancelAll = async (email) => {
    if (!window.confirm(
      `Cancel ALL Stripe subscriptions for ${email}?\n\nThe app says this user is NOT premium, but Stripe is still charging them. This stops every recurring charge immediately.`
    )) return;
    setFixing(email);
    try {
      const { data } = await api.post(`/admin/billing/cancel-all-subs/${encodeURIComponent(email)}`);
      toast.success(`Canceled ${data.canceled.length} subscription(s) for ${email}.`);
      scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setFixing(null);
    }
  };

  const refundLast = async (email) => {
    if (!window.confirm(
      `Refund the MOST RECENT charge for ${email}?\n\nThis sends real money back via Stripe. Combine with "Cancel all subs" to fully resolve a charged-after-cancel complaint.`
    )) return;
    setFixing(email);
    try {
      const { data } = await api.post(`/admin/billing/refund-last-charge/${encodeURIComponent(email)}`);
      toast.success(`Refunded $${data.amount_usd.toFixed(2)} (charge from ${String(data.charge_date).slice(0, 10)}) to ${email}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setFixing(null);
    }
  };

  return (
    <div className="mt-8 border border-zinc-200 bg-white p-6" data-testid="billing-health-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="gf-overline">Billing health</div>
          <h2 className="font-display font-black text-lg tracking-tight mt-1">Duplicate subscription scanner.</h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Finds customers with more than one chargeable Stripe subscription — the cause of
            &quot;still charged after canceling&quot; complaints. One click cancels the extras.
          </p>
        </div>
        <Button
          onClick={scan}
          disabled={scanning}
          data-testid="billing-scan-btn"
          className="gf-sharp bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wide"
        >
          <Detective size={14} className="mr-1.5" />
          {scanning ? "Scanning…" : "Scan customers"}
        </Button>
      </div>

      <div className="mt-5 pt-4 border-t border-zinc-100">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          Manual: stop all recurring charges for one customer
        </div>
        <p className="text-xs text-zinc-500 mt-1 max-w-xl">
          Works even when the scan doesn&apos;t flag them (e.g. the app thinks they&apos;re an active
          subscriber but they asked to cancel). Finds every Stripe subscription by email and cancels immediately.
        </p>
        <div className="mt-2 flex gap-2 max-w-md">
          <input
            type="email"
            value={manualEmail}
            onChange={(e) => setManualEmail(e.target.value)}
            placeholder="customer@email.com"
            data-testid="manual-cancel-email-input"
            className="flex-1 border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-zinc-900"
          />
          <button
            onClick={() => manualEmail.trim() && cancelAll(manualEmail.trim().toLowerCase())}
            disabled={!manualEmail.trim() || fixing === manualEmail.trim().toLowerCase()}
            data-testid="manual-cancel-all-btn"
            className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 hover:bg-red-50 px-4 text-xs font-bold uppercase tracking-wide disabled:opacity-40"
          >
            <Scissors size={13} /> Cancel all subs
          </button>
          <button
            onClick={() => manualEmail.trim() && refundLast(manualEmail.trim().toLowerCase())}
            disabled={!manualEmail.trim() || fixing === manualEmail.trim().toLowerCase()}
            data-testid="manual-refund-btn"
            className="inline-flex items-center gap-1.5 border border-zinc-900 text-zinc-900 hover:bg-zinc-100 px-4 text-xs font-bold uppercase tracking-wide disabled:opacity-40"
          >
            Refund last charge
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-4" data-testid="billing-scan-result">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
            {result.users_scanned} users scanned · {result.duplicates.length} double-billed · {(result.zombies || []).length} charged-after-cancel
            {result.scan_errors > 0 && ` · ${result.scan_errors} scan errors`}
          </div>
          {(result.scan_error_emails || []).length > 0 && (
            <div className="mt-2 text-[11px] font-mono text-amber-700" data-testid="scan-error-emails">
              Could not check (Stripe lookup failed — use the manual box for these): {result.scan_error_emails.join(", ")}
            </div>
          )}
          {(result.zombies || []).length > 0 && (
            <div className="mt-3 border border-red-300 divide-y divide-red-100" data-testid="zombie-list">
              <div className="px-4 py-2 bg-red-100 text-[10px] font-bold uppercase tracking-widest text-red-800">
                Still being charged after cancel/refund — cancel their subs now
              </div>
              {result.zombies.map((d) => (
                <div key={`z-${d.email}`} className="px-4 py-3 bg-red-50/60 flex items-start justify-between gap-3 flex-wrap" data-testid={`zombie-row-${d.email}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-900">{d.email}</div>
                    <div className="text-[11px] font-mono text-zinc-600 mt-1 space-y-0.5">
                      {d.subs.map((s) => (
                        <div key={s.id}>{s.plan} · {fmtMoney(s.amount_usd)}/mo · {s.status} · {s.id}</div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => cancelAll(d.email)}
                    disabled={fixing === d.email}
                    data-testid={`cancel-all-${d.email}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-red-700 hover:underline disabled:opacity-50"
                  >
                    <Scissors size={13} /> {fixing === d.email ? "Canceling…" : "Cancel all subs"}
                  </button>
                </div>
              ))}
            </div>
          )}
          {result.duplicates.length > 0 && (
            <div className="mt-3 border border-red-200 divide-y divide-red-100">
              {result.duplicates.map((d) => (
                <div key={d.email} className="px-4 py-3 bg-red-50/40 flex items-start justify-between gap-3 flex-wrap" data-testid={`dup-row-${d.email}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-900">{d.email}</div>
                    <div className="text-[11px] font-mono text-zinc-600 mt-1 space-y-0.5">
                      {d.subs.map((s, i) => (
                        <div key={s.id}>
                          {i === 0 ? "KEEP " : "EXTRA"} · {s.plan} · {fmtMoney(s.amount_usd)}/mo · {s.status}
                          {s.cancel_at_period_end ? " · ends at period" : ""} · {s.id}
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => fix(d.email)}
                    disabled={fixing === d.email}
                    data-testid={`fix-dups-${d.email}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-red-700 hover:underline disabled:opacity-50"
                  >
                    <Scissors size={13} /> {fixing === d.email ? "Fixing…" : "Cancel extras"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
